import React from 'react';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import PropTypes from 'prop-types';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import classNames from 'classnames';
import BigPicklistControl from 'Components/ComponentLibrary/Components/Inputs/Picklist/BigPicklistControl';
import SurveyStepHeader from 'Views/Onboarding/Components/SurveyStepHeader';
import MetaField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import MoreOptionsIndicator
  from 'Views/Onboarding/Pages/Survey/SurveyStep/StepLibrary/QualitativeQuestion/MoreOptionsIndicator';
import useScrollTrigger from '@material-ui/core/useScrollTrigger';
import useScrollTopOnMount from 'Hooks/useScrollTopOnMount';
import Waypoint from 'react-waypoint';
import useMobileLayout from 'Hooks/useMobileLayout';
import onboardingStyles from '../../../../../Onboarding/Pages/Survey/SurveyStep/StepLibrary/QualitativeQuestion/styles';

const styles = (theme) => ({
  ...onboardingStyles(theme),
  root: {
    padding: theme.spacing(2),
    display: 'flex',
    maxWidth: '400px',
    alignItems: 'center',
    flexDirection: 'column',
    margin: 'auto',
  },
  rootDesktop: {
    maxWidth: '600px',
    minWidth: '400px',
  },
  surveyStepHeader: {
    padding: 0,
  },
  surveyStepHeaderTitle: {
    fontWeight: theme.typography.fontWeightBold,
    marginBottom: theme.spacing(4),
  },
  surveyStepHeaderSubtitle: {
    fontSize: theme.typography.h6.fontSize,
    fontWeight: theme.typography.fontWeightBold,
    marginTop: 0,
    marginBottom: theme.spacing(4),
  },
  noBottomMargin: {
    marginBottom: 0,
  },
});

function Layout({
  classes,
}) {
  const {
    resource,
    metaOverrideSet,
    handleChange,
  } = useMetaLayoutContext();
  const isScrolled = useScrollTrigger({ disableHysteresis: true });
  const [isFullyScrolled, setIsFullyScrolled] = React.useState(false);

  useScrollTopOnMount();

  const isMultiLineInput = resource?.meta().field('answer').attr('type') === 'string';
  const showPlaceholder = isMultiLineInput && resource.attr('placeholderText');

  const isMultiPicklist = resource?.meta().field('options').attr('type') === 'multipicklist';
  const isPicklist = resource?.meta().field('options').attr('type') === 'picklist';
  const defaultValue = isMultiPicklist ? [] : undefined;

  const isMobile = useMobileLayout();

  return (
    <div className={classNames(classes.root, {
      [classes.rootDesktop]: !isMobile,
    })}
    >
      <SurveyStepHeader
        title={resource.attr('title') || resource.attr('primaryText')}
        titleTest="wizard-step-title"
        subtitle={resource.attr('subtitle') || resource.attr('secondaryText')}
        classes={{
          root: classes.surveyStepHeader,
          title: classes.surveyStepHeaderTitle,
          subtitle: classes.surveyStepHeaderSubtitle,
        }}
      />
      <Box className={classNames(classes.content, classes.noBottomMargin)}>
        {(isMultiPicklist || isPicklist) && (
          <BigPicklistControl
            className={classes.question}
            selectId={'options'}
            multiple={isMultiPicklist}
            testName={'qualitative-question'}
            value={resource?.attr('options') || defaultValue}
            noOptionsPlaceholderText={resource?.attr('noOptionsPlaceholderText') || 'No options'}
            options={resource
              .meta()
              .field('options')
              .options()
              .list()
              .map(option => ({
                value: option.attr('id'),
                name: option.attr('prompt'),
              }))}
            onChange={(values, value) => {
              const metaField = metaOverrideSet.applyOverrides(resource.meta().field('options'));
              const transformValue = metaField.attr('_transformValue') || null;

              let updatedValues = values;
              if (transformValue && isMultiPicklist) {
                updatedValues = transformValue(value, values, resource);
              }

              return handleChange('options', updatedValues);
            }}
          />
        )}
        {isMultiLineInput && (
          <MetaField
            attr={'answer'}
            InputProps={{
              multiline: true,
              placeholder: showPlaceholder ? resource.attr('placeholderText') : '',
            }}
          />
        )}
        <MoreOptionsIndicator
          className={classNames(classes.moreOptionsIndicator, {
            [classes.moreOptionsIndicatorHidden]: isScrolled || isFullyScrolled,
          })}
        />
      </Box>
      <Waypoint
        onLeave={() => {
          setIsFullyScrolled(false);
        }}
        onEnter={() => {
          setIsFullyScrolled(true);
        }}
      />
    </div>
  );
}

Layout.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(Layout);
